import { theme as antdTheme } from "antd";
import themeColors from "./themeColors.json";

export { themeColors };
/** Semantic tokens for the active color mode. */
export function getSemanticTokens(mode = "light") {
  const isDark = mode === "dark";
  const surface = isDark ? themeColors.dark : themeColors.light;

  return {
    brand: themeColors.brand,
    status: themeColors.status,
    bg: {
      1: surface.bg1,
      2: surface.bg2,
      3: surface.bg3,
      4: surface.bg4,
    },
    dashboardBg: surface.dashboardBg,
    text: {
      1: surface.text1,
      2: surface.text2,
      3: surface.text3,
      4: surface.text4,
    },
    border: surface.bg3,
    borderStrong: surface.bg4,
    primary: themeColors.brand.primary,
    primaryHover: themeColors.brand.primary2,
    primaryMuted: themeColors.brand.primaryLight,
    primarySurface: isDark ? "#2A2540" : themeColors.brand.primaryLight,
    primarySoft: isDark
      ? themeColors.brand.primaryDark
      : themeColors.brand.primaryLight,
    primarySoftBorder: isDark ? themeColors.dark.bg3 : themeColors.light.bg4,
  };
}

/** Badge surface tokens — add variants under themeColors.badge in themeColors.json. */
export function getBadgeCssVariables(mode = "light") {
  const isDark = mode === "dark";
  const vars = {};

  Object.entries(themeColors.badge).forEach(([variant, tones]) => {
    const tone = isDark ? tones.dark : tones.light;
    vars[`--pp-badge-${variant}-bg`] = tone.bg;
    vars[`--pp-badge-${variant}-text`] = tone.text;
  });

  return vars;
}

/** CSS custom properties — single source wired into Tailwind + globals.css. */
export function getCssVariableDefinitions(mode = "light") {
  const isDark = mode === "dark";
  const t = getSemanticTokens(mode);

  return {
    "--pp-primary": themeColors.brand.primary,
    "--pp-primary-dark": themeColors.brand.primaryDark,
    "--pp-primary-light": themeColors.brand.primaryLight,
    "--pp-primary-2": themeColors.brand.primary2,
    "--pp-error": themeColors.status.error,
    "--pp-success": themeColors.status.success,
    "--pp-warning": themeColors.status.warning,
    "--pp-bg-1": t.bg[1],
    "--pp-bg-2": t.bg[2],
    "--pp-dashboard-bg": t.dashboardBg,
    "--pp-bg-3": t.bg[3],
    "--pp-bg-4": t.bg[4],
    "--pp-text-1": t.text[1],
    "--pp-text-2": t.text[2],
    "--pp-text-3": t.text[3],
    "--pp-text-4": t.text[4],
    "--pp-border": t.border,
    "--pp-border-strong": t.borderStrong,
    "--pp-primary-surface": t.primarySurface,
    "--pp-primary-soft": t.primarySoft,
    "--pp-primary-soft-border": t.primarySoftBorder,
    "--pp-primary-disabled-bg": `color-mix(in srgb, ${themeColors.brand.primary} 50%, ${t.bg[1]})`,
    "--pp-primary-disabled-text": isDark
      ? themeColors.dark.primaryDisabledText
      : `color-mix(in srgb, ${themeColors.light.primaryDisabledText} 85%, var(--pp-primary-disabled-bg))`,
    ...getBadgeCssVariables(mode),
  };
}

export function applyThemeVariables(mode = "light") {
  const root = document.documentElement;
  const vars = getCssVariableDefinitions(mode);

  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  root.classList.toggle("dark", mode === "dark");
}

/** Ant Design v5 theme derived from the same tokens. */
export function getAntDesignTheme(mode = "light") {
  const t = getSemanticTokens(mode);
  const isDark = mode === "dark";

  return {
    hashed: false,
    cssVar: { key: "pp" },
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: themeColors.brand.primary,
      colorPrimaryHover: themeColors.brand.primary2,
      colorPrimaryActive: themeColors.brand.primaryDark,
      colorLink: themeColors.brand.primary,
      colorLinkHover: themeColors.brand.primary2,
      colorLinkActive: themeColors.brand.primaryDark,
      colorError: themeColors.status.error,
      colorSuccess: themeColors.status.success,
      colorWarning: themeColors.status.warning,
      colorBgBase: t.bg[1],
      colorBgContainer: t.bg[1],
      colorBgLayout: t.dashboardBg,
      colorBorder: t.border,
      colorText: t.text[1],
      colorTextSecondary: t.text[2],
      colorTextTertiary: t.text[3],
      colorTextQuaternary: t.text[4],
      fontFamily: "Inter, sans-serif",
      borderRadius: 8,
      controlHeight: 44,
    },
    components: {
      Button: {
        primaryShadow: "none",
        defaultShadow: "none",
        fontWeight: 500,
        controlHeight: 44,
        borderRadius: 40,
        borderRadiusLG: 40,
        borderRadiusSM: 40,
        colorPrimary: themeColors.brand.primary,
        colorPrimaryHover: themeColors.brand.primary2,
        colorPrimaryActive: themeColors.brand.primaryDark,
        colorPrimaryBorder: themeColors.brand.primary,
        colorPrimaryBorderHover: themeColors.brand.primary2,
      },
      Input: {
        controlHeight: 56,
        borderRadius: 12,
        paddingBlock: 8,
        paddingInline: 16,
        colorBgContainer: t.bg[1],
        colorBorder: t.border,
        colorText: t.text[1],
        colorTextPlaceholder: t.text[4],
        activeBorderColor: themeColors.brand.primary,
        hoverBorderColor: themeColors.brand.primary2,
      },
      Table: {
        headerBg: t.bg[2],
        headerColor: t.text[2],
        rowSelectedHoverBg: t.bg[2],
        cellPaddingBlock: 20,
        borderColor: t.border,
      },
      Tabs: {
        itemColor: t.text[3],
        itemSelectedColor: themeColors.brand.primary,
        inkBarColor: themeColors.brand.primary,
      },
      Menu: {
        itemSelectedColor: themeColors.brand.primary,
        itemSelectedBg: t.primarySurface,
        itemHoverBg: t.bg[2],
      },
      Pagination: {
        itemActiveBg: t.primarySurface,
        itemActiveColor: themeColors.brand.primary,
      },
    },
  };
}
